import { get } from 'lodash';
import { Body, Heading } from '@lendi-ui/typography';

import { ENTITY_TYPE, FIELD_PATHS, LICENSED_BUILDER_QUESTIONS } from '@funnels/constants';
import { useFormValues } from '@funnels/hooks';
import { AssetEntityIndex, PostProductSelectContract, RelatedParty } from '@funnels/types';
import {
  ActionButtonsWrapper,
  Container,
  ContentWrapper,
  FunnelGrid,
  FunnelGridUnit,
  HeadingWrapper,
  NextArrowAction,
} from '@funnels/ui-components';
import {
  ContactCompanyAddress,
  ContactCompanyName,
  ContactFirstName,
  ContactLandlineNumber,
  ContactLastName,
  ContactLicenseNumber,
  ContactMobileNumber,
  ContactTitle,
} from '@funnels/ui-components/PostProductSelect';
import { getQuestionDisplayFields } from '@funnels/utils';

export const LicnesedBuilderLayout = (props: AssetEntityIndex) => {
  const { assetIndex } = props;
  const [formValues] = useFormValues();

  const relatedParties: RelatedParty[] = get(formValues, FIELD_PATHS.RELATED_PARTIES(), []);
  const ppsContracts: PostProductSelectContract[] = get(formValues, FIELD_PATHS.POST_PRODUCT_SELECTS(), []);
  const asset = get(formValues, FIELD_PATHS.ASSET(assetIndex));
  /**
   * Beginning of locating correct question and relatedParty Index
   */
  // targetAssetId is the key to find the correct questions and relatedParty Index
  const targetAssetId = get(formValues, FIELD_PATHS.ASSET_ID(assetIndex));
  const lincensedBuilderSelectContract = ppsContracts.find(
    (pps) =>
      get({ postProductSelect: [pps] }, FIELD_PATHS.POST_PRODUCT_SELECT_ENTITY_TYPE(0)) ===
        ENTITY_TYPE.LICENSED_BUILDER &&
      get({ postProductSelect: [pps] }, FIELD_PATHS.POST_PRODUCT_SELECT_ENTITY_ID(0)) === targetAssetId
  );
  const relatedPartyIndex = relatedParties.findIndex(
    (relatedParty) =>
      get({ relatedParties: [relatedParty] }, FIELD_PATHS.RELATED_PARTY_TYPE(0)) === ENTITY_TYPE.LICENSED_BUILDER &&
      get({ relatedParties: [relatedParty] }, FIELD_PATHS.RELATED_PARTY_ASSET_ID(0)) === targetAssetId
  );
  const questions = get(
    { postProductSelect: [lincensedBuilderSelectContract] },
    FIELD_PATHS.POST_PRODUCT_SELECT_QUESTIONS(0),
    []
  ) as LICENSED_BUILDER_QUESTIONS[];
  /**
   * End of locating correct question and relatedParty Index
   */

  const hasQuestion = getQuestionDisplayFields<LICENSED_BUILDER_QUESTIONS>(questions);
  const address = get({ assets: [asset] }, FIELD_PATHS.ASSET_FORMATTED_ADDRESS(0));
  const heading = 'Please give us the details of your licensed builder';

  return (
    <Container>
      <HeadingWrapper>
        <Heading size="md">{heading}</Heading>
        <Body size="md">{address}</Body>
      </HeadingWrapper>
      <ContentWrapper>
        {(hasQuestion.TITLE || hasQuestion.FIRST_NAME) && (
          <FunnelGridUnit mt="sm">
            <FunnelGrid gutter="xxs">
              <FunnelGridUnit size={hasQuestion.TITLE && hasQuestion.FIRST_NAME ? 0.3 : 1}>
                {hasQuestion.TITLE && <ContactTitle relatedPartyIndex={relatedPartyIndex} isOptional />}
              </FunnelGridUnit>
              <FunnelGridUnit size={hasQuestion.TITLE && hasQuestion.FIRST_NAME ? 0.7 : 1}>
                {hasQuestion.FIRST_NAME && <ContactFirstName relatedPartyIndex={relatedPartyIndex} isOptional />}
              </FunnelGridUnit>
            </FunnelGrid>
          </FunnelGridUnit>
        )}
        {hasQuestion.LAST_NAME && (
          <FunnelGridUnit mt="sm">
            <ContactLastName relatedPartyIndex={relatedPartyIndex} isOptional />
          </FunnelGridUnit>
        )}
        {hasQuestion.BUSINESS_NAME && (
          <FunnelGridUnit mt="sm">
            <ContactCompanyName label={'Business name'} relatedPartyIndex={relatedPartyIndex} isOptional />
          </FunnelGridUnit>
        )}
        {hasQuestion.ADDRESS && (
          <FunnelGridUnit mt="sm">
            <ContactCompanyAddress relatedPartyIndex={relatedPartyIndex} isOptional />
          </FunnelGridUnit>
        )}
        {hasQuestion.LICENSED_BUILDER_LANDLINE && (
          <FunnelGridUnit mt="sm">
            <ContactLandlineNumber relatedPartyIndex={relatedPartyIndex} isOptional />
          </FunnelGridUnit>
        )}
        {hasQuestion.PHONE && (
          <FunnelGridUnit mt="sm">
            <ContactMobileNumber relatedPartyIndex={relatedPartyIndex} isOptional />
          </FunnelGridUnit>
        )}
        {hasQuestion.LICENSE_NUMBER && (
          <FunnelGridUnit mt="sm">
            <ContactLicenseNumber relatedPartyIndex={relatedPartyIndex} isOptional />
          </FunnelGridUnit>
        )}
      </ContentWrapper>
      <FunnelGridUnit>
        <ActionButtonsWrapper>
          <NextArrowAction variant={'primary'} isFullWidth />
        </ActionButtonsWrapper>
      </FunnelGridUnit>
    </Container>
  );
};
